import {
  ScheduleTaskType,
  parseDateOnly,
  scheduleNonWorkDates,
  scheduleTaskWorkdays,
  type ScheduleTaskInput,
} from '@construction/shared';

/**
 * 甘特图的几何（CR-080）。纯函数，和画法分开，好测。
 *
 * 时间轴从开工那周的**周日**排到完工那周的**周六** —— 和客户原表（Buildertrend 月历）的周划分一致，
 * 对表时「第几周」对得上。所有日期都是日期型的值（当天 UTC 零点），不按任何时区换算（CR-031）。
 */

export const DAY_MS = 86_400_000;

export interface GanttRange {
  /** 第一天（周日）UTC 零点 */
  start: number;
  /** 最后一天（周六）UTC 零点 */
  end: number;
  days: number;
}

export function ganttRange(tasks: ReadonlyArray<Pick<ScheduleTaskInput, 'plannedStart' | 'plannedFinish'>>): GanttRange | null {
  let min = Infinity;
  let max = -Infinity;
  for (const t of tasks) {
    const s = parseDateOnly(t.plannedStart);
    const f = parseDateOnly(t.plannedFinish);
    if (s === null || f === null || f < s) continue;
    min = Math.min(min, s);
    max = Math.max(max, f);
  }
  if (!Number.isFinite(min)) return null;
  const start = min - new Date(min).getUTCDay() * DAY_MS;
  const end = max + (6 - new Date(max).getUTCDay()) * DAY_MS;
  return { start, end, days: (end - start) / DAY_MS + 1 };
}

/** 月份段：[第一天, 天数]，首尾两段可能不满一个月 */
export function monthSegments(range: GanttRange): Array<{ start: number; days: number }> {
  const out: Array<{ start: number; days: number }> = [];
  let cursor = range.start;
  while (cursor <= range.end) {
    const d = new Date(cursor);
    const next = Math.min(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1), range.end + DAY_MS);
    out.push({ start: cursor, days: (next - cursor) / DAY_MS });
    cursor = next;
  }
  return out;
}

/** 周：每个周日一段，`index` 从 1 数（W1 = 开工那周） */
export function weekStarts(range: GanttRange): Array<{ start: number; index: number }> {
  const out: Array<{ start: number; index: number }> = [];
  for (let d = range.start, i = 1; d <= range.end; d += 7 * DAY_MS, i++) out.push({ start: d, index: i });
  return out;
}

export function dayList(range: GanttRange): number[] {
  const out: number[] = [];
  for (let d = range.start; d <= range.end; d += DAY_MS) out.push(d);
  return out;
}

export interface ScheduleSummary {
  start: string;
  finish: string;
  calendarDays: number;
  /** 开工到完工之间的工作日，扣停工日 */
  workdays: number;
  counts: Record<ScheduleTaskType, number>;
}

/** 统计卡上的几个数。和甘特图用同一份停工日、同一份工作日历 */
export function summarizeSchedule(
  tasks: ReadonlyArray<Pick<ScheduleTaskInput, 'type' | 'plannedStart' | 'plannedFinish'>>,
  workdays: readonly number[],
): ScheduleSummary | null {
  const valid = tasks.filter((t) => {
    const s = parseDateOnly(t.plannedStart);
    const f = parseDateOnly(t.plannedFinish);
    return s !== null && f !== null && f >= s;
  });
  if (valid.length === 0) return null;
  const starts = valid.map((t) => parseDateOnly(t.plannedStart)!);
  const finishes = valid.map((t) => parseDateOnly(t.plannedFinish)!);
  const start = new Date(Math.min(...starts)).toISOString().slice(0, 10);
  const finish = new Date(Math.max(...finishes)).toISOString().slice(0, 10);
  const nonWork = new Set(scheduleNonWorkDates(valid, workdays));
  const counts: Record<ScheduleTaskType, number> = { WORK: 0, INSPECTION: 0, NONWORK: 0 };
  for (const t of valid) counts[t.type]++;
  return {
    start,
    finish,
    calendarDays: (Math.max(...finishes) - Math.min(...starts)) / DAY_MS + 1,
    workdays: scheduleTaskWorkdays({ type: ScheduleTaskType.WORK, plannedStart: start, plannedFinish: finish }, workdays, nonWork) ?? 0,
    counts,
  };
}

/** 今天是整个工期的第几周（W1 = 开工那周），和甘特图表头的 W 编号一致；不在范围里给 null */
export function projectWeek(range: GanttRange, today: string): { week: number; total: number } | null {
  const t = parseDateOnly(today);
  if (t === null || t < range.start || t > range.end) return null;
  return { week: Math.floor((t - range.start) / DAY_MS / 7) + 1, total: range.days / 7 };
}

/** 今天在做的（不含停工项）。App 进度表顶上那一行「今天：…」 */
export function activeOn<T extends Pick<ScheduleTaskInput, 'type' | 'plannedStart' | 'plannedFinish'>>(
  tasks: readonly T[],
  today: string,
): T[] {
  const t = parseDateOnly(today);
  if (t === null) return [];
  return tasks.filter((task) => {
    const s = parseDateOnly(task.plannedStart);
    const f = parseDateOnly(task.plannedFinish);
    return task.type !== ScheduleTaskType.NONWORK && s !== null && f !== null && s <= t && t <= f;
  });
}
